<template>
  <div class="collections-table-wrapper">
    <div v-if="collections.length === 0" class="text-center text-muted py-5">
      <p>No collections available</p>
    </div>
    <div v-else class="table-responsive">
      <table class="table table-hover collections-table">
        <thead class="table-header-sticky">
          <tr>
            <th class="sortable" @click="sortBy('name')">
              Collection Name
              <i v-if="sortField === 'name'" :class="sortOrder === 'asc' ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
            </th>
            <th class="sortable" @click="sortBy('parentBiobankName')">
              Service Provider
              <i v-if="sortField === 'parentBiobankName'" :class="sortOrder === 'asc' ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
            </th>
            <th class="sortable" @click="sortBy('type')">
              Type
              <i v-if="sortField === 'type'" :class="sortOrder === 'asc' ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
            </th>
            <th v-if="showSampleCount" class="sortable" @click="sortBy('size')">
              Sample Count
              <i v-if="sortField === 'size'" :class="sortOrder === 'asc' ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
            </th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="collection in sortedCollections"
            :key="collection.id"
            @click="selectRow(collection)"
            class="collection-row"
          >
            <td class="collection-name">
              <strong>{{ collection.name }}</strong>
              <span v-if="collection.acronym" class="text-muted ms-2">({{ collection.acronym }})</span>
            </td>
            <td class="biobank-name">{{ collection.parentBiobankName }}</td>
            <td class="collection-type">
              {{ getCollectionType(collection.type) || 'Standard' }}
            </td>
            <td v-if="showSampleCount" class="sample-count">
              {{ collection.size || '-' }}
            </td>
            <td class="description" :title="collection.description">
              {{ truncateText(collection.description, 80) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps({
  collections: {
    type: Array as () => any[],
    default: () => []
  },
  biobanks: {
    type: Object,
    default: () => ({})
  },
  showSampleCount: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['row-click']);

const sortField = ref('name');
const sortOrder = ref('asc');

const sortedCollections = computed(() => {
  if (!props.collections || props.collections.length === 0) return [];
  
  const sorted = [...props.collections].sort((a, b) => {
    let aVal = a[sortField.value];
    let bVal = b[sortField.value];
    
    // Handle nested fields like type (array)
    if (sortField.value === 'type' && Array.isArray(aVal)) {
      aVal = aVal[0]?.label || '';
      bVal = Array.isArray(bVal) ? bVal[0]?.label || '' : '';
    }
    
    // Handle undefined/null
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    
    // String comparison
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal as string).toLowerCase();
      return sortOrder.value === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    
    // Number comparison
    return sortOrder.value === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });
  
  return sorted;
});

const sortBy = (field: string) => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortOrder.value = 'asc';
  }
};

const selectRow = (collection: any) => {
  emit('row-click', collection);
};

const truncateText = (text: string, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const getCollectionType = (typeArray: any[]): string => {
  if (!typeArray || !Array.isArray(typeArray) || typeArray.length === 0) {
    return '';
  }
  return typeArray[0]?.label || typeArray[0]?.name || '';
};
</script>

<style scoped>
.collections-table-wrapper {
  width: 100%;
}

.table-responsive {
  overflow-x: auto;
}

.collections-table {
  margin-bottom: 0;
  font-size: 0.95rem;
}

.table-header-sticky {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 10;
  border-bottom: 2px solid #dee2e6;
}

.table-header-sticky th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #333;
  padding: 0.75rem;
  border: none;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #e9ecef;
}

.sortable i {
  margin-left: 0.5rem;
  font-size: 0.8rem;
  opacity: 0.5;
}

.collection-row {
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.collection-row:hover {
  background-color: #f0f0f0;
}

.collection-name {
  font-weight: 500;
  max-width: 250px;
  word-break: break-word;
}

.biobank-name {
  font-weight: 500;
  color: #666;
}

.collection-type {
  color: #999;
  font-size: 0.9rem;
}

.description {
  color: #777;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sample-count {
  text-align: center;
  color: #666;
}

.text-muted {
  color: #999 !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .collections-table {
    font-size: 0.85rem;
  }
  
  .table-header-sticky th {
    padding: 0.5rem 0.25rem;
  }
  
  .collection-name {
    max-width: 150px;
  }
  
  .description {
    display: none;
  }
}
</style>
